import { useEffect } from "react";

/**
 * JsonLd — injects a `<script type="application/ld+json">` block into <head>
 * for the lifetime of the component, then removes it on unmount.
 *
 * Why a hook + script tag instead of dangerouslySetInnerHTML in the body?
 *   - Google, Bing, and AI crawlers expect schema.org JSON-LD inside <head>
 *     (or anywhere, but <head> is the convention). Injecting from JS works
 *     for crawlers that execute JS (Googlebot, Bingbot Adaptive). For static
 *     pre-rendering / SSR we'll snapshot the rendered <head> at build time.
 *   - Each page can pass arbitrary objects; multiple `<JsonLd>` blocks per
 *     page are fine and recommended (Organization + Product + FAQPage).
 *
 * Usage:
 *   <JsonLd id="org" data={{ "@context": "https://schema.org", "@type": "Organization", ... }} />
 */
type JsonLdProps = {
    /** Stable DOM id so the cleanup phase can find/remove this exact block. */
    id: string;
    /** Any JSON-LD-shaped object (or array of objects for @graph-style use). */
    data: Record<string, unknown> | Record<string, unknown>[];
};

const JsonLd: React.FC<JsonLdProps> = ({ id, data }) => {
    useEffect(() => {
        const scriptId = `jsonld-${id}`;
        let script = document.getElementById(scriptId) as HTMLScriptElement | null;
        if (!script) {
            script = document.createElement("script");
            script.id = scriptId;
            script.type = "application/ld+json";
            document.head.appendChild(script);
        }
        try {
            script.textContent = JSON.stringify(data);
        } catch (error) {
            console.error(`Failed to serialize JSON-LD block "${id}":`, error);
            script.textContent = "";
        }

        return () => {
            document.getElementById(scriptId)?.remove();
        };
    }, [id, data]);

    return null;
};

export default JsonLd;
